import { cn } from "./utils";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { Circle } from "lucide-react";
import * as React from "react";

const RadioCardGroup = React.forwardRef<
    React.ElementRef<typeof RadioGroupPrimitive.Root>,
    React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
    return <RadioGroupPrimitive.Root className={cn("flex flex-col space-y-3", className)} {...props} ref={ref} />;
});
RadioCardGroup.displayName = "RadioCardGroup";

interface RadioCardItemProps extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> {
    label: string;
    description?: string;
    icon?: React.ComponentType<{ className?: string }>;
}

const RadioCardItem = React.forwardRef<React.ElementRef<typeof RadioGroupPrimitive.Item>, RadioCardItemProps>(
    ({ className, label, description, icon: Icon, value, ...props }, ref) => {
        return (
            <RadioGroupPrimitive.Item
                ref={ref}
                value={value}
                className={cn(
                    "flex items-start space-x-3 p-4 rounded-lg border cursor-pointer transition-colors bg-white ring-offset-white text-left w-full",
                    "data-[state=checked]:border-indigo-300 data-[state=checked]:ring-2 data-[state=checked]:ring-indigo-300 data-[state=checked]:ring-offset-2",
                    "data-[state=unchecked]:border-border data-[state=unchecked]:hover:bg-accent",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 focus-visible:ring-offset-2",
                    "disabled:cursor-not-allowed disabled:opacity-50",
                    className
                )}
                {...props}
            >
                <div className="flex items-center justify-center aspect-square h-5 w-5 rounded-full border border-indigo-600 text-indigo-600 mt-0.5 shrink-0">
                    <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
                        <Circle className="h-2.5 w-2.5 fill-current text-current" />
                    </RadioGroupPrimitive.Indicator>
                </div>
                <div className="flex-1">
                    <div className="flex items-center gap-2 cursor-pointer">
                        {Icon && <Icon className="h-4 w-4" />}
                        <span className="font-medium">{label}</span>
                    </div>
                    {description && <p className="text-sm text-muted-foreground mt-1">{description}</p>}
                </div>
            </RadioGroupPrimitive.Item>
        );
    }
);
RadioCardItem.displayName = "RadioCardItem";

export { RadioCardGroup, RadioCardItem };
